import { useState, useEffect } from "react";
import { useRoute, Link } from "wouter";
import type { ProjectDetail } from "@/api/client";
import { api, isComicAnalysis, isLegacyAnalysis } from "@/api/client";
import { StatusBadge } from "@/components/StatusBadge";
import { ImageGallery } from "@/components/ImageGallery";
import { ComicAnalysisDisplay } from "@/components/ComicAnalysisDisplay";
import { LifeKLineChart } from "@/components/lifekline/LifeKLineChart";
import { AnalysisResult } from "@/components/lifekline/AnalysisResult";
import type { LifeDestinyResult, BaziInput } from "@/lifekline/types";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, FileText, Brain, ImageIcon, TrendingUp, Download } from "lucide-react";
import { formatDistanceToNow } from "@/lib/utils";

// Type guard for lifekline analysis
function isLifeklineAnalysis(analysis: any): analysis is LifeDestinyResult {
  return analysis !== null && 'chartData' in analysis && 'analysis' in analysis;
}

export function ProjectDetailPage() {
  const [, params] = useRoute("/projects/:id");
  const projectId = params?.id;

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProject = async () => {
    if (!projectId) return;

    const response = await api.projects.get(projectId);
    if (response.success && response.data) {
      setProject(response.data);
    } else {
      setError(response.error || "Failed to load project");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProject();

    // Poll for updates while project is in progress
    const interval = setInterval(() => {
      if (project?.status === 'analyzing' || project?.status === 'generating' || project?.status === 'pending') {
        fetchProject();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [projectId, project?.status]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-destructive">{error || "项目不存在"}</p>
        <Link href="/">
          <Button>返回首页</Button>
        </Link>
      </div>
    );
  }

  const completedImages = project.images.filter((img) => img.status === 'completed').length;
  const totalImages = project.images.length;
  
  // Check if this is a lifekline project
  const isLifeklineProject = project.templateId === 'lifekline';
  const lifeklineResult = isLifeklineProject && project.analysis ? project.analysis as unknown as LifeDestinyResult : null;
  
  // Try to parse bazi input from inputText for lifekline projects
  let baziInput: BaziInput | null = null;
  if (isLifeklineProject) {
    try {
      baziInput = JSON.parse(project.inputText);
    } catch {
      // Ignore parse errors
    }
  }

  const handleExportReport = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border no-print">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Link href="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-foreground">{project.name}</h1>
              <StatusBadge status={project.status} />
            </div>
            <p className="text-sm text-muted-foreground">
              创建于 {formatDistanceToNow(new Date(project.createdAt))}
            </p>
          </div>
          
          {/* Export button for lifekline */}
          {isLifeklineProject && project.status === 'completed' && lifeklineResult && (
            <Button variant="outline" size="sm" onClick={handleExportReport}>
              <Download className="w-4 h-4 mr-1" />
              导出报告
            </Button>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-4 py-8 space-y-8">
        {/* Progress indicator for generating status */}
        {(project.status === 'analyzing' || project.status === 'generating' || project.status === 'pending') && (
          <div className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-primary animate-spin" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">
                  {isLifeklineProject 
                    ? '正在推演命盘...' 
                    : project.status === 'analyzing' 
                      ? '正在分析文本...' 
                      : '正在生成图片...'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {isLifeklineProject 
                    ? 'AI 正在根据八字信息生成人生K线图和分析报告，预计需要 30-60 秒'
                    : project.status === 'analyzing'
                      ? 'AI 正在理解您的文本内容'
                      : `已完成 ${completedImages} / ${totalImages} 张图片`}
                </p>
              </div>
            </div>
            
            {!isLifeklineProject && project.status === 'generating' && totalImages > 0 && (
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-500"
                  style={{ width: `${(completedImages / totalImages) * 100}%` }}
                />
              </div>
            )}
          </div>
        )}

        {/* Error message */}
        {project.status === 'failed' && project.error && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-6">
            <h3 className="font-semibold text-destructive mb-2">生成失败</h3>
            <p className="text-sm text-destructive/80">{project.error}</p>
          </div>
        )}

        {/* Lifekline specific content */}
        {isLifeklineProject && project.status === 'completed' && lifeklineResult && (
          <>
            {/* K-Line Chart */}
            <section className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-500" />
                </div>
                <h2 className="text-lg font-semibold text-foreground">人生运势K线图</h2>
              </div>
              <LifeKLineChart 
                data={lifeklineResult.chartData} 
                userName={baziInput?.name}
              />
            </section>
            
            {/* Analysis Report */}
            <AnalysisResult 
              analysis={lifeklineResult.analysis}
              chartData={lifeklineResult.chartData}
              userName={baziInput?.name}
            />
          </>
        )}

        {/* Non-lifekline content */}
        {!isLifeklineProject && (
          <>
            {/* Input Text */}
            <section className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-500" />
                </div>
                <h2 className="text-lg font-semibold text-foreground">输入文本</h2>
              </div>
              <p className="text-muted-foreground whitespace-pre-wrap">
                {project.inputText}
              </p>
            </section>

            {/* Analysis - Comic format */}
            {project.analysis && isComicAnalysis(project.analysis) && (
              <ComicAnalysisDisplay analysis={project.analysis} />
            )}

            {/* Analysis - Legacy format */}
            {project.analysis && isLegacyAnalysis(project.analysis) && (
              <section className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-purple-500" />
                  </div>
                  <h2 className="text-lg font-semibold text-foreground">AI 分析结果</h2>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-muted-foreground">计划生成:</span>
                    <span className="font-medium">{project.analysis.imageCount} 张图片</span>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">分析理由:</p>
                    <p className="text-foreground">{project.analysis.reasoning}</p>
                  </div>
                </div>
              </section>
            )}

            {/* Images */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">生成的图片</h2>
                  <p className="text-sm text-muted-foreground">
                    {completedImages} / {totalImages} 已完成
                  </p>
                </div>
              </div>
              
              <ImageGallery images={project.images} />
            </section>
          </>
        )}
      </main>
    </div>
  );
}
